import { getVariableColor } from '@/composables/theme'

interface Tag {
  raw: string
  tag: string
  index: number
  color: [string, string]
}

export type TagMap = Record<string, Tag>
export type TagsReplaceData = Record<string, string>
export type TagParser = (tag: Tag) => (data: string) => string

function clearTag(tag: string) {
  return tag
    .trim()
    .replaceAll(/[\s\n]/gi, '_')
    .toLowerCase()
}

interface GenerateTemplateOptions {
  tagMarker: '#' | '@' | '$' | '!' | '%'
  displayParser?: TagParser
  tags?: TagMap
}

const DEFAULT_OPTIONS: GenerateTemplateOptions = {
  tagMarker: '#',
}

export function generateTagsHtml(tag: Tag) {
  const color = tag.color
  const styles = `
    color: ${color[0]};
    background-color: ${color[1]};
    border-radius: 4px;
  `.replace(/\s/g, '')

  return (data: string) =>
    `<span style="${styles}" data-tag="${tag.tag}" data-colors="${color.join(
      ',',
    )}" data-test="ok">#${data?.trim() || tag.tag}</span>`
}

function getTags(data: RegExpStringIterator<RegExpExecArray>): TagMap {
  const keys: TagMap = {}
  let currentMatchIndex = 0
  for (const match of data) {
    const [raw, tag] = match
    const parsedTag = clearTag(tag)
    if (keys[parsedTag])
      continue

    keys[parsedTag] = {
      raw: raw
        .trim()
        .replaceAll(/[\[\]\#]/gi, '')
        .toLowerCase(),
      tag: parsedTag,
      index: currentMatchIndex,
      color: getVariableColor(currentMatchIndex),
    }
    currentMatchIndex++
  }
  return keys
}

// Allowed tags for formatting (bold, italic, underline, line breaks, spans for color)

export function generateTemplate(
  raw: string | null,
  options = DEFAULT_OPTIONS,
) {
  if (!raw)
    return null
  if (typeof raw !== 'string')
    throw new Error('Template must be a string')

  // First, extract existing tag spans and convert them to #[tag] pattern
  let processedRaw = raw
  const spanPattern = /<span[^>]*data-label="([^"]*)"[^>]*>#[^<]*<\/span>/gi
  const existingTags = []

  processedRaw = processedRaw.replace(spanPattern, (match, label) => {
    existingTags.push(label)
    return `#[${label}]`
  })

  // Preserve empty paragraphs and line breaks by protecting them during processing
  // Handle both patterns: <p></p> and <p><br></p>
  const emptyParagraphPattern = /<p([^>]*)><\/p>/gi
  const emptyParagraphWithBrPattern = /<p([^>]*)><br[^>]*><\/p>/gi
  const emptyParagraphs: Array<{ placeholder: string; content: string }> = []
  let emptyParagraphIndex = 0

  // First handle empty paragraphs with <br> tags
  processedRaw = processedRaw.replace(emptyParagraphWithBrPattern, (match) => {
    const placeholder = `__EMPTY_PARAGRAPH_${emptyParagraphIndex++}__`
    emptyParagraphs.push({ placeholder, content: match })
    return placeholder
  })

  // Then handle completely empty paragraphs and convert them to have <br> tags
  processedRaw = processedRaw.replace(emptyParagraphPattern, (match, attributes) => {
    const placeholder = `__EMPTY_PARAGRAPH_${emptyParagraphIndex++}__`
    const contentWithBr = `<p${attributes}><br class="ProseMirror-trailingBreak"></p>`
    emptyParagraphs.push({ placeholder, content: contentWithBr })
    return placeholder
  })

  const templatePattern = new RegExp(`${options.tagMarker}\\[(.*?)\\]`, 'gi')
  const tagData = getTags(processedRaw.matchAll(templatePattern))
  const keys = Object.keys(tagData)

  // If no valid placeholders found, return the raw content as-is
  if (keys.length === 0) {
    return {
      transformer: () => raw,
      tagData,
      display: raw,
      raw,
      variables: [],
    }
  }

  let templateMask = processedRaw.replaceAll(templatePattern, (_, tag) => {
    const tagClear = clearTag(tag)
    const tagInfo = tagData[tagClear]
    if (tagInfo) {
      return options.displayParser
        ? options.displayParser(tagInfo)(tagInfo.tag)
        : `${options.tagMarker}${tagInfo.raw}`
    }
    return tagClear
  })

  // Restore empty paragraphs
  emptyParagraphs.forEach(({ placeholder, content }) => {
    templateMask = templateMask.replace(placeholder, content)
  })

  const transformer = (keysAndData: TagsReplaceData, parser?: TagParser) => {
    let result = processedRaw.replaceAll(templatePattern, (_, tag) => {
      const tagClear = clearTag(tag)
      const tagInfo = tagData[tagClear]
      if (!tagInfo)
        return tag

      const content
        = keysAndData[tagClear]?.trim() !== ''
          ? keysAndData[tagClear]
          : tagInfo.tag

      return parser ? parser(tagInfo)(content) : content
    })

    // Restore empty paragraphs
    emptyParagraphs.forEach(({ placeholder, content }) => {
      result = result.replace(placeholder, content)
    })

    return result
  }

  return {
    transformer,
    tagData,
    display: templateMask,
    raw,
    variables: keys,
  }
}
